import { useQuery } from '@tanstack/react-query'

import { get, getPaginated } from '@/lib/api'

import type { TrackType, TimeStatus } from '@company/shared'


// ==================== Types ====================

export type { TrackType }

export type GroupByOption = 'project' | 'stage' | 'track'
export type SortByOption = 'deadline' | 'waitingTime' | 'priority' | 'createdAt'
export type SortOrder = 'asc' | 'desc'
export type QuickFilter = 'all' | 'overdue' | 'dueToday' | 'dueThisWeek' | 'atRisk'
export type Priority = 'low' | 'medium' | 'high' | 'urgent'

export interface EnhancedTaskItem {
  issueId: string
  issueTitle: string
  projectId: string
  projectName: string
  currentStage: string
  track?: TrackType
  trackStatus?: string
  role: 'owner' | 'participant' | 'assignee'
  deadline: string | null
  deadlineCountdown: number | null
  waitingTime: number
  timeStatus: TimeStatus
  priority: Priority
  requiresAction: boolean
  createdAt: string
}

export interface GroupedTasks {
  groupKey: string
  groupName: string
  tasks: EnhancedTaskItem[]
  overdueCount: number
  totalCount: number
}

export interface EnhancedTasksParams {
  groupBy?: GroupByOption
  sortBy?: SortByOption
  sortOrder?: SortOrder
  filter?: QuickFilter
  page?: number
  pageSize?: number
}

// ==================== Query Keys ====================

export const enhancedTasksKeys = {
  all: ['enhanced-tasks'] as const,
  list: (params: EnhancedTasksParams) => [...enhancedTasksKeys.all, 'list', params] as const,
  grouped: (params: EnhancedTasksParams) => [...enhancedTasksKeys.all, 'grouped', params] as const,
}

// ==================== Display Names ====================

export const GROUP_BY_OPTIONS: { value: GroupByOption; label: string }[] = [
  { value: 'project', label: '按项目' },
  { value: 'stage', label: '按阶段' },
  { value: 'track', label: '按轨道' },
]

export const SORT_BY_OPTIONS: { value: SortByOption; label: string }[] = [
  { value: 'deadline', label: '截止日期' },
  { value: 'waitingTime', label: '等待时间' },
  { value: 'priority', label: '优先级' },
  { value: 'createdAt', label: '创建时间' },
]

export const QUICK_FILTER_OPTIONS: { value: QuickFilter; label: string; color?: string }[] = [
  { value: 'all', label: '全部' },
  { value: 'overdue', label: '已超时', color: 'text-red-600' },
  { value: 'dueToday', label: '今日到期', color: 'text-amber-600' },
  { value: 'dueThisWeek', label: '本周到期', color: 'text-blue-600' },
  { value: 'atRisk', label: '有风险', color: 'text-orange-600' },
]

export const PRIORITY_DISPLAY_NAMES: Record<Priority, string> = {
  low: '低',
  medium: '中',
  high: '高',
  urgent: '紧急',
}

export const TIME_STATUS_DISPLAY_NAMES: Record<TimeStatus, string> = {
  on_track: '正常',
  approaching_deadline: '即将到期',
  overdue: '已超时',
}

export function getPriorityDisplayName(priority: Priority): string {
  return PRIORITY_DISPLAY_NAMES[priority] ?? priority
}

export function getTimeStatusDisplayName(status: TimeStatus): string {
  return TIME_STATUS_DISPLAY_NAMES[status] ?? status
}

// ==================== Hooks ====================

/**
 * 获取增强任务列表
 * Requirements: 6.1, 6.2, 6.6
 */
export function useEnhancedTasks(params: EnhancedTasksParams = {}) {
  return useQuery({
    queryKey: enhancedTasksKeys.list(params),
    queryFn: () =>
      getPaginated<EnhancedTaskItem>('/pm/workflow/my-tasks-enhanced', params as Record<string, unknown>),
  })
}

/**
 * 获取分组任务列表
 * Requirements: 6.1
 */
export function useGroupedTasks(params: EnhancedTasksParams = {}) {
  return useQuery({
    queryKey: enhancedTasksKeys.grouped(params),
    queryFn: () =>
      get<GroupedTasks[]>('/pm/workflow/my-tasks-grouped', params as Record<string, unknown>),
  })
}

/**
 * 格式化截止日期倒计时
 */
export function formatDeadlineCountdown(ms: number | null): string {
  if (ms === null) { return '-' }

  const isOverdue = ms < 0
  const absMs = Math.abs(ms)
  const hours = Math.floor(absMs / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24

  let text = ''
  if (days > 0) {
    text = `${days}天${remainingHours > 0 ? `${remainingHours}小时` : ''}`
  } else if (hours > 0) {
    text = `${hours}小时`
  } else {
    const minutes = Math.floor(absMs / (1000 * 60))
    text = `${minutes}分钟`
  }

  return isOverdue ? `超时 ${text}` : `剩余 ${text}`
}

/**
 * 格式化等待时间
 */
export function formatWaitingTime(ms: number): string {
  const hours = Math.floor(ms / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)

  if (days > 0) {
    return `${days}天${hours % 24}小时`
  }
  if (hours > 0) {
    return `${hours}小时`
  }
  const minutes = Math.floor(ms / (1000 * 60))
  return `${minutes}分钟`
}
